import { getCareSettingProfile } from './careSettingProfiles.js';

export const FOUNDATION_JOURNEY_IDS = Object.freeze([
  'kch-day-surgery',
  'kch-maternity',
  'kch-outpatient-department',
  'kch-emergency-department',
  'kch-imaging-diagnostics',
]);

const COMPLETION = Object.freeze({
  compact: true,
  label: 'Journey summary',
  title: "You've reached the end of this guide",
  description: 'You do not need to open everything. Use the parts that are helpful to you and return whenever you need them.',
});

const QUALITY_BY_JOURNEY_ID = Object.freeze({
  'kch-day-surgery': {
    careSettingProfileKey: 'daySurgery',
    stages: [
      {
        id: 'before-you-arrive',
        title: 'Before you arrive',
        description: 'Check your preparation, fasting, medicines, arrival information and what to bring.',
        phraseKeys: ['preAssessment', 'nilByMouth', 'consent'],
        linkIds: ['fasting', 'where-to-go', 'preparing'],
        questionIndexes: [0, 1],
      },
      {
        id: 'procedure-and-recovery',
        title: 'Your procedure and recovery',
        description: 'Understand who may be involved and what you may wish to ask before and immediately after your procedure.',
        phraseKeys: ['cannula', 'theatre', 'recovery', 'analgesia', 'antiemetic'],
        linkIds: [],
        questionIndexes: [2, 3],
      },
      {
        id: 'going-home',
        title: 'Going home',
        description: 'Plan for discharge, transport, support at home and what to do if you have concerns.',
        phraseKeys: ['takeHomeMedicines', 'followUp', 'safetyNetting'],
        linkIds: ['going-home'],
        questionIndexes: [4, 5, 6],
      },
    ],
  },
  'kch-maternity': {
    careSettingProfileKey: 'maternity',
    stages: [
      {
        id: 'during-pregnancy',
        title: 'During pregnancy',
        description: 'Understand who is coordinating your care and when other members of the maternity team may become involved.',
        phraseKeys: ['maternityTriage'],
        linkIds: ['where-to-go'],
        questionIndexes: [0, 2],
      },
      {
        id: 'preparing-for-birth',
        title: 'Preparing for birth',
        description: 'Prepare for labour, discuss pain relief and understand support and visiting arrangements.',
        phraseKeys: ['birthCentre', 'inductionOfLabour'],
        linkIds: ['preparing-for-birth', 'visiting-and-support'],
        questionIndexes: [1, 4],
      },
      {
        id: 'labour-and-birth',
        title: 'Labour and birth',
        description: 'Know when to contact the maternity team and what may happen if plans for birth change.',
        phraseKeys: ['labourWard', 'deliverySuite', 'ctg', 'epidural', 'artificialRuptureOfMembranes', 'assistedBirth', 'caesareanSection'],
        linkIds: ['when-to-contact'],
        questionIndexes: [3, 7],
      },
      {
        id: 'after-birth',
        title: 'After birth',
        description: 'Find support for you and your baby before and after going home.',
        phraseKeys: ['skinToSkin', 'delayedCordClamping', 'postnatalWard', 'neonatalUnit', 'breastfeedingSupport'],
        linkIds: ['feeding-support', 'going-home'],
        questionIndexes: [5, 6, 8],
      },
    ],
  },
  'kch-outpatient-department': {
    careSettingProfileKey: 'outpatients',
    stages: [
      {
        id: 'before-your-appointment',
        title: 'Before your appointment',
        description: 'Check where to go, what to bring and any adjustments or support you may need.',
        phraseKeys: [],
        linkIds: ['arrival', 'appointment', 'access'],
        questionIndexes: [0, 1],
      },
      {
        id: 'at-the-clinic',
        title: 'At the clinic',
        description: 'Understand who you may meet, what tests may be discussed and what the next steps could be.',
        phraseKeys: ['referral', 'review', 'bloodTests', 'scan'],
        linkIds: [],
        questionIndexes: [2, 3],
      },
      {
        id: 'after-your-appointment',
        title: 'After your appointment',
        description: 'Clarify results, letters, prescriptions, follow-up and who to contact if things change.',
        phraseKeys: ['resultsPending', 'followUp', 'safetyNetting'],
        linkIds: ['after'],
        questionIndexes: [4, 5, 6],
      },
    ],
  },
  'kch-emergency-department': {
    careSettingProfileKey: 'emergencyDepartment',
    stages: [
      {
        id: 'arrival-and-first-assessment',
        title: 'Arrival and first assessment',
        description: 'Understand registration, initial assessment, prioritisation and what to do if you feel worse.',
        phraseKeys: ['triage', 'observations'],
        linkIds: ['arrival', 'waiting'],
        questionIndexes: [0, 2],
      },
      {
        id: 'assessment-and-treatment',
        title: 'Assessment and treatment',
        description: 'Ask about tests, medicines and what the team is considering while you are in the department.',
        phraseKeys: ['cannula', 'bloodTests', 'scan', 'review'],
        linkIds: ['support'],
        questionIndexes: [1, 3, 4],
      },
      {
        id: 'what-happens-next',
        title: 'What happens next',
        description: 'Understand whether you may go home, need follow-up or be admitted to hospital.',
        phraseKeys: ['admission', 'followUp', 'safetyNetting'],
        linkIds: ['leaving'],
        questionIndexes: [5, 6],
      },
    ],
  },
  'kch-imaging-diagnostics': {
    careSettingProfileKey: 'imagingDiagnostics',
    stages: [
      {
        id: 'before-your-test',
        title: 'Before your test',
        description: 'Check preparation instructions, medicines, fasting, accessibility needs and where to go.',
        phraseKeys: [],
        linkIds: ['prepare', 'where', 'access'],
        questionIndexes: [0, 1, 3, 4, 6],
      },
      {
        id: 'during-your-visit',
        title: 'During your visit',
        description: 'Understand who may be involved and what to expect from the appointment itself.',
        phraseKeys: ['scan', 'cannula'],
        linkIds: [],
        questionIndexes: [2],
      },
      {
        id: 'results-and-follow-up',
        title: 'Results and follow-up',
        description: 'Find out how your results will be reviewed, communicated and followed up.',
        phraseKeys: ['resultsPending', 'followUp'],
        linkIds: ['results'],
        questionIndexes: [5],
      },
    ],
  },
});

// Canonical English patient-facing stage overrides; clinical inventory consumes this unchanged.
export const FOUNDATION_JOURNEY_QUALITY = QUALITY_BY_JOURNEY_ID;

export function applyFoundationJourneyQuality(journey) {
  if (!journey) return journey;
  const quality = QUALITY_BY_JOURNEY_ID[journey.id];
  if (!quality) return journey;

  const profile = getCareSettingProfile(quality.careSettingProfileKey, { trustId: journey.trustId });
  const coreRoleKeys = profile?.coreRoleKeys || [];

  return {
    ...journey,
    careSettingProfileKey: quality.careSettingProfileKey,
    completion: journey.completion || COMPLETION,
    stages: quality.stages,
    roles: coreRoleKeys.map((roleKey) => ({ id: roleKey })),
  };
}
